<template>
  <div class="w-full max-w-3xl mx-auto">
    <Chart :type="'line'" :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup>
import { defineProps, computed } from 'vue';
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement, PointElement, LinearScale, CategoryScale, LineController } from 'chart.js';
import { Chart } from 'vue-chartjs';
import { calculateAgeInDays } from '@/functions/utils';
import { useDataStore } from '@/stores/useDataStore';
import { roundFloat } from '@/functions/utils';

const dataStore = useDataStore();

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale
);

const props = defineProps({
  prelevements: {
    type: Array,
    required: true
  },
  analyses: {
    type: Array,
    required: true
  }
});

const chartData = computed(() => {
  const startdaycode = dataStore.selectedCulture?.startdaycode?.startday_code || '';
  const sortedPrelevements = props.prelevements
    .filter(p => p.absorbance_880nm !== null)
    .sort((a, b) => a.daycode.localeCompare(b.daycode));

  const extractDaycodeFromBatchcode = (batchcode) => {
    const parts = batchcode?.split('-');
    if (!parts || parts.length < 2) return null;
    return `${parts[0]}-${parts[1]}`;
  };

  const volumeUtilePBR = dataStore.selectedCulture?.pbr.volume

  const calculateNewValue = (p) => {
    return p.absorbance_880nm !== undefined && p.vol_recolte
      ? roundFloat(p.absorbance_880nm * (volumeUtilePBR - p.vol_recolte) / volumeUtilePBR)
      : null
  };

  const combinedData = sortedPrelevements.flatMap(p => {
    const age = Number(calculateAgeInDays(p.daycode, startdaycode));
    const points = [{ x: age, y: p.absorbance_880nm }];

    const newValue = calculateNewValue(p);
    if (newValue !== null) {
      points.push({ x: age, y: newValue });
    }

    return points;
  });

  const concentrationData = props.analyses
    .filter(a =>
      a.aire_echantillon_dilue !== null &&
      a.preparation_echantillon_masse_pesee !== null &&
      a.preparation_echantillon_volume_total !== null
    )
    .flatMap(a => {
      const concentration = dataStore.getConcentration(a);
      const points = [];
      const daycodes = [
        { source: 'Prélèvement', daycode: a.prelevement?.daycode },
        // { source: 'Récolte', daycode: extractDaycodeFromBatchcode(a.batch?.batch_code) }
      ];
      daycodes.forEach(({ source, daycode }) => {
        if (daycode) {
          const age = calculateAgeInDays(daycode, startdaycode);
          if (!isNaN(age)) {
            points.push({
              x: age,
              y: concentration,
              source
            });
          }
        }
      });
      return points;
    })
    .sort((a, b) => a.x - b.x);

  return {
    datasets: [
      {
        label: 'Concentration',
        data: concentrationData,
        borderColor: 'transparent',
        backgroundColor: 'orange',
        pointRadius: 5,
        pointHoverRadius: 6,
        pointBackgroundColor: 'orange',
        pointBorderColor: 'orange',
        pointBorderWidth: 1,
        showLine: false,
        yAxisID: 'concentration',
      },
      {
        label: 'Absorbance',
        data: combinedData,
        borderColor: 'rgb(0, 184, 190)',
        fill: false,
        tension: 0.3,
        pointRadius: 6,
        pointBorderWidth: 2,
        pointBackgroundColor: 'rgb(0, 184, 190)',
        pointBorderColor: 'rgb(0, 184, 190)',
        yAxisID: 'absorbance',
      }
    ]
  };
});

const chartOptions = computed(() => {
  const getMin = (arr) => Math.min(...arr.filter(val => val !== null));
  const getMax = (arr) => Math.max(...arr.filter(val => val !== null));

  const absorbanceValues = chartData.value.datasets[1].data.map(p => p.y);
  const concentrationValues = chartData.value.datasets[0].data.map(p => p.y);

  const minConcentration = parseFloat((getMin(concentrationValues) - (getMax(concentrationValues) - getMin(concentrationValues)) * 0.1).toFixed(2));
  const maxConcentration = parseFloat((getMax(concentrationValues) + (getMax(concentrationValues) - getMin(concentrationValues)) * 0.1).toFixed(2));

  return {
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const item = tooltipItems[0];
            return `Âge : ${item.parsed.x} jours`;
          },
          label: (tooltipItem) => {
            const datasetLabel = tooltipItem.dataset.label;
            const yValue = tooltipItem.parsed.y;
            const source = tooltipItem.raw.source;
            if (datasetLabel === 'Concentration') {
              return `Concentration : ${yValue} % (${source})`;
            } else if (datasetLabel === 'Absorbance') {
              return `Absorbance : ${yValue}`;
            }
            return `${datasetLabel}: ${yValue}`;
          }
        }
      },
      legend: {
        position: 'top',
        labels: {
          generateLabels: () => [
            {
              text: 'Absorbance',
              fillStyle: 'rgb(0, 184, 190)',
              strokeStyle: 'rgb(0, 184, 190)',
              lineWidth: 3
            },
            {
              text: 'Concentration',
              fillStyle: 'orange',
              strokeStyle: 'orange',
              lineWidth: 3
            }
          ]
        }
      }
    },
    scales: {
      absorbance: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Absorbance'
        },
        min: 0,
        max: Math.ceil(getMax(absorbanceValues) + 1),
        position: 'left',
      },
      concentration: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Concentration'
        },
        min: minConcentration,
        max: maxConcentration,
        position: 'right',
      },
      x: {
        type: 'linear',
        title: {
          display: true,
          text: 'Âge (jours)'
        },
        ticks: {
          stepSize: 1
        }
      }
    }
  };
});
</script>

<style scoped>
div {
  height: 400px;
}
</style>
